import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { ArrowLeft, Loader2, UploadCloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { UploadDocumentApi } from "../apis/documents";
import RichTextEditor from "./RichTextEditor";
import { useContentSearch } from "./useContentSearch";
import SearchBar from "./SearchBar";

const initialForm = {
  title: "",
  subtitle: "",
  description: "",
  content_text: "",
  category: "",
  tags: "",
  keywords: "",
  headings: "",
  subheadings: "",
  is_pinned: false,
};

// ── CDN loaders ───────────────────────────────────────────────
const PDFJS_VERSION = "3.11.174";
const PDFJS_CDN     = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const WORKER_CDN    = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
const MAMMOTH_CDN   = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function getPdfjsLib(): Promise<any> {
  await loadScript(PDFJS_CDN);
  const lib = (window as any).pdfjsLib;
  if (!lib) throw new Error("pdfjsLib not found");
  lib.GlobalWorkerOptions.workerSrc = WORKER_CDN;
  return lib;
}

async function getMammoth(): Promise<any> {
  await loadScript(MAMMOTH_CDN);
  const m = (window as any).mammoth;
  if (!m) throw new Error("mammoth not found");
  return m;
}

export default function UploadDocument() {
  const navigate = useNavigate();
  const [form, setForm]             = useState(initialForm);
  const [file, setFile]             = useState<File | null>(null);
  const [saving, setSaving]         = useState(false);
  const [extracting, setExtracting] = useState(false);

  // ── Search (shared hook) ──────────────────────────────────
  const search = useContentSearch();

  // Capture the contentEditable div inside RichTextEditor
  const captureEditorRef = useCallback((wrapperEl: HTMLDivElement | null) => {
    if (!wrapperEl) return;
    const ce = wrapperEl.querySelector("[contenteditable='true']") as HTMLDivElement | null;
    if (ce) search.contentRef.current = ce;
  }, [search.contentRef]);

  // Pre-load libraries in background on mount
  useEffect(() => {
    getPdfjsLib().catch(() => {});
    getMammoth().catch(() => {});
  }, []);

  const update = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const titleFromFilename = (name: string) =>
    name
      .replace(/\.[^.]+$/, "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // ── Field parsers ─────────────────────────────────────────
  const parseFieldsFromText = (text: string) => {
    const lines       = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const title       = lines[0] || "";
    const subtitle    = lines.slice(1, 6).find((l) => l.length <= 120 && !/^([-*•]|\d+[.)])/.test(l)) || "";
    const description = lines.find((l) => l.length > 60) || lines.slice(0, 3).join(" ").slice(0, 300);
    const headings    = lines.filter((l) => l.length >= 4 && l.length <= 80 && !/[.!?,;]$/.test(l) && !/^([-*•]|\d+[.)])/.test(l)).slice(1, 8).join(", ");
    const subheadings = lines.filter((l) => l.length > 80 && l.length <= 150 && !/[.!?]$/.test(l)).slice(0, 5).join(", ");
    const stopWords   = new Set(["this","that","with","from","have","will","your","they","been","were","when","what","which","their","there","about","would","could","should","also","into","than","then","some","more","very","just","over","such","each","after","before","these","those","other","only","both"]);
    const wordFreq: Record<string, number> = {};
    text.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).forEach((w) => {
      if (w.length >= 4 && !stopWords.has(w)) wordFreq[w] = (wordFreq[w] || 0) + 1;
    });
    const sorted   = Object.entries(wordFreq).sort((a, b) => b[1] - a[1]);
    const keywords = sorted.slice(0, 10).map(([w]) => w).join(", ");
    const tags     = sorted.slice(0, 5).map(([w]) => w).join(", ");
    return { title, subtitle, description, headings, subheadings, keywords, tags };
  };

  const parseFieldsFromHtml = (html: string) => {
    const doc         = new DOMParser().parseFromString(html, "text/html");
    const title       = doc.querySelector("h1")?.textContent?.trim() || "";
    const subtitle    = doc.querySelector("h2")?.textContent?.trim() || "";
    const description = Array.from(doc.querySelectorAll("p")).map((p) => p.textContent?.trim() || "").find((t) => t.length > 60) || "";
    const headings    = Array.from(doc.querySelectorAll("h1,h2,h3")).map((h) => h.textContent?.trim() || "").filter(Boolean).slice(0, 8).join(", ");
    const subheadings = Array.from(doc.querySelectorAll("h4,h5,h6")).map((h) => h.textContent?.trim() || "").filter(Boolean).slice(0, 5).join(", ");
    const plain       = doc.body.textContent || "";
    const stopWords   = new Set(["this","that","with","from","have","will","your","they","been","were","when","what","which","their","there","about","would","could","should","also","into","than","then","some","more","very","just","over","such","each","after","before","these","those","other","only","both"]);
    const wordFreq: Record<string, number> = {};
    plain.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).forEach((w) => {
      if (w.length >= 4 && !stopWords.has(w)) wordFreq[w] = (wordFreq[w] || 0) + 1;
    });
    const sorted   = Object.entries(wordFreq).sort((a, b) => b[1] - a[1]);
    const keywords = sorted.slice(0, 10).map(([w]) => w).join(", ");
    const tags     = sorted.slice(0, 5).map(([w]) => w).join(", ");
    return { title, subtitle, description, headings, subheadings, keywords, tags };
  };

  const textToEditorHtml = (text: string): string => {
    const lines = text.split(/\r?\n/);
    const html: string[] = [];
    let wroteTitle = false;
    let inList = false;
    const closeList = () => { if (inList) { html.push("</ul>"); inList = false; } };
    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) { closeList(); return; }
      const bullet = line.match(/^([-*•]|\d+[.)])\s+(.*)$/);
      if (bullet) {
        if (!inList) { html.push("<ul>"); inList = true; }
        html.push(`<li>${bullet[2]}</li>`);
        return;
      }
      closeList();
      if (!wroteTitle) { html.push(`<h1>${line}</h1>`); wroteTitle = true; }
      else if (line.length <= 90 && !/[.,;:]$/.test(line)) html.push(`<h2>${line}</h2>`);
      else html.push(`<p>${line}</p>`);
    });
    closeList();
    return html.join("\n");
  };

  // ── Extractors ────────────────────────────────────────────
  const extractPdf = async (f: File): Promise<string> => {
    const lib  = await getPdfjsLib();
    const pdf  = await lib.getDocument({ data: await f.arrayBuffer() }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page    = await pdf.getPage(i);
      const content = await page.getTextContent();
      const text    = content.items.map((it: any) => it.str).join(" ").trim();
      if (text) pages.push(text);
    }
    return pages.join("\n\n");
  };

  const extractDocx = async (f: File): Promise<string> => {
    const mammoth = await getMammoth();
    const result  = await mammoth.convertToHtml({ arrayBuffer: await f.arrayBuffer() });
    return result.value;
  };

  // ── File change handler ───────────────────────────────────
  const handleFileChange = async (selected: File | null) => {
    setFile(selected);
    if (!selected) return;

    const name   = selected.name.toLowerCase();
    const isTxt  = selected.type.startsWith("text/") || name.endsWith(".txt") || name.endsWith(".md");
    const isPdf  = selected.type === "application/pdf" || name.endsWith(".pdf");
    const isDocx = name.endsWith(".docx") || name.endsWith(".doc");

    if (isTxt) {
      const text   = await selected.text();
      const fields = parseFieldsFromText(text);
      setForm((prev) => ({
        ...prev,
        title:        prev.title        || fields.title        || titleFromFilename(selected.name),
        subtitle:     prev.subtitle     || fields.subtitle,
        description:  prev.description  || fields.description,
        headings:     prev.headings     || fields.headings,
        subheadings:  prev.subheadings  || fields.subheadings,
        keywords:     prev.keywords     || fields.keywords,
        tags:         prev.tags         || fields.tags,
        content_text: prev.content_text || textToEditorHtml(text),
      }));
      toast.success("Text file loaded — all fields auto-filled");
      return;
    }

    if (isPdf) {
      setExtracting(true);
      const tid = toast.loading("Extracting PDF content...");
      try {
        const raw = await extractPdf(selected);
        if (!raw.trim()) { toast.error("No readable text — PDF may be image-based.", { id: tid }); return; }
        const fields = parseFieldsFromText(raw);
        setForm((prev) => ({
          ...prev,
          title:        prev.title        || fields.title        || titleFromFilename(selected.name),
          subtitle:     prev.subtitle     || fields.subtitle,
          description:  prev.description  || fields.description,
          headings:     prev.headings     || fields.headings,
          subheadings:  prev.subheadings  || fields.subheadings,
          keywords:     prev.keywords     || fields.keywords,
          tags:         prev.tags         || fields.tags,
          content_text: prev.content_text || textToEditorHtml(raw),
        }));
        toast.success("PDF loaded — all fields auto-filled", { id: tid });
      } catch (err) {
        console.error(err);
        toast.error("Could not read PDF.", { id: tid });
      } finally {
        setExtracting(false);
      }
      return;
    }

    if (isDocx) {
      setExtracting(true);
      const tid = toast.loading("Extracting Word document...");
      try {
        const html = await extractDocx(selected);
        if (!html.trim()) { toast.error("No readable content in this Word document.", { id: tid }); return; }
        const fields = parseFieldsFromHtml(html);
        setForm((prev) => ({
          ...prev,
          title:        prev.title        || fields.title        || titleFromFilename(selected.name),
          subtitle:     prev.subtitle     || fields.subtitle,
          description:  prev.description  || fields.description,
          headings:     prev.headings     || fields.headings,
          subheadings:  prev.subheadings  || fields.subheadings,
          keywords:     prev.keywords     || fields.keywords,
          tags:         prev.tags         || fields.tags,
          content_text: prev.content_text || html,
        }));
        toast.success("Word document loaded — all fields auto-filled", { id: tid });
      } catch (err) {
        console.error(err);
        toast.error("Could not read Word document.", { id: tid });
      } finally {
        setExtracting(false);
      }
      return;
    }

    toast.success("File selected. Metadata will be saved on upload.");
  };

  // ── Submit ────────────────────────────────────────────────
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload: any = { ...form };
      if (file) payload.file = file;
      const response = await UploadDocumentApi(payload);
      const id = response?.data?.id;
      toast.success("Document uploaded");
      navigate(id ? `/documents/${id}` : "/documents");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload document");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="mt-8 max-w-5xl pb-10">
      <button
        onClick={() => navigate("/documents")}
        className="mb-4 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <form onSubmit={submit} className="rounded-lg border border-slate-800 bg-slate-900/70 p-5">
        {/* Header */}
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300">
            <UploadCloud />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">Upload Document</h1>
            <p className="text-sm text-slate-400">Add searchable admin knowledge base content.</p>
          </div>
        </div>

        {/* Meta fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Title *"                       value={form.title}       onChange={(e) => update("title",       e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Subtitle"                       value={form.subtitle}    onChange={(e) => update("subtitle",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Category"                       value={form.category}    onChange={(e) => update("category",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Tags, comma separated"          value={form.tags}        onChange={(e) => update("tags",        e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Keywords, comma separated"      value={form.keywords}    onChange={(e) => update("keywords",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white" placeholder="Headings, comma separated"      value={form.headings}    onChange={(e) => update("headings",    e.target.value)} />
          <input className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white md:col-span-2" placeholder="Subheadings, comma separated" value={form.subheadings} onChange={(e) => update("subheadings", e.target.value)} />
          <textarea className="min-h-28 rounded-lg border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white md:col-span-2" placeholder="Description" value={form.description} onChange={(e) => update("description", e.target.value)} />
        </div>

        {/* Content editor + search bar */}
        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <label className="text-sm font-semibold text-slate-200">Content editor</label>
            <div className="flex flex-wrap items-center gap-2">
              {form.content_text && (
                <SearchBar
                  searchQuery={search.searchQuery}
                  setSearchQuery={search.setSearchQuery}
                  matchCount={search.matchCount}
                  currentMatch={search.currentMatch}
                  goNext={search.goNext}
                  goPrev={search.goPrev}
                  clearSearch={search.clearSearch}
                />
              )}
              <span className="text-xs text-slate-500">Rich text is searchable after saving</span>
            </div>
          </div>

          {/* Wrap to capture contentEditable ref */}
          <div ref={captureEditorRef}>
            <RichTextEditor value={form.content_text} onChange={(value) => update("content_text", value)} />
          </div>
        </div>

        {/* File picker */}
        <div className="mt-4 rounded-lg border border-dashed border-slate-700 bg-slate-950 p-4">
          <label className="flex cursor-pointer flex-col items-center gap-2 text-center">
            <input
              type="file"
              accept=".pdf,.txt,.doc,.docx,.md"
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              className="hidden"
            />
            {extracting ? (
              <div className="flex flex-col items-center gap-2 py-2">
                <Loader2 size={28} className="animate-spin text-blue-400" />
                <span className="text-sm font-medium text-blue-300">Extracting content, please wait...</span>
              </div>
            ) : file ? (
              <div className="flex flex-col items-center gap-1 py-2">
                <UploadCloud size={24} className="text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-300">{file.name}</span>
                <span className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB &mdash; click to change</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 py-3">
                <UploadCloud size={28} className="text-slate-500" />
                <span className="text-sm font-medium text-slate-300">Click to choose a file</span>
                <span className="text-xs text-slate-500">PDF, DOC, DOCX and TXT — content loads directly into the editor</span>
              </div>
            )}
          </label>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.is_pinned} onChange={(e) => update("is_pinned", e.target.checked)} />
          Pin this document
        </label>

        <div className="mt-6 flex justify-end">
          <button
            disabled={saving || extracting}
            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60"
          >
            {saving ? "Uploading..." : extracting ? "Extracting..." : "Upload Document"}
          </button>
        </div>
      </form>
    </div>
  );
}
